import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import CanvaDesign from "../../ai-content/components/CanvaDesign";
import { getBaseUrl } from "../../api/api";

// ─── Insert into Quill ──────────────────────────────────────────────────────────

const insertImageIntoQuill = (quill, file) => {
  if (!quill) return;
  const reader = new FileReader();
  reader.onload = () => {
    const range = quill.getSelection(true);
    const index = range?.index ?? quill.getLength();
    quill.insertEmbed(
      index,
      "image",
      reader.result
    );
    quill.setSelection(index + 1, 0, "silent");
    quill.focus();
  };
  reader.readAsDataURL(file);
};

// ─── Component ─────────────────────────────────────────────────────────────────

interface Props {
  projectId: string;
  navigateToUrl: (url: string) => void;
  quillRef: React.MutableRefObject<any>;
  openRef?: React.MutableRefObject<(() => void) | null>;
}

const EmailCanvaPicker = ({ projectId, navigateToUrl, quillRef, openRef }: Props) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Compute fixed position on open (escape modal overflow)
  const handleOpen = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen((v) => !v);
  };

  // Expose open function to parent via ref
  useEffect(() => {
    if (openRef) {
      openRef.current = () => {
        if (triggerRef.current) {
          const rect = triggerRef.current.getBoundingClientRect();
          setPos({ top: rect.bottom + 4, left: rect.left });
        }
        setOpen(true);
      };
    }
  });

  // Close on outside click
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  return (
    <span className="relative inline-flex items-center align-middle">
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        title={t("Insert Canva")}
        onClick={handleOpen}
        className={`inline-flex items-center justify-center w-[28px] h-[24px] rounded transition-colors focus:outline-none cursor-pointer select-none
          ${open ? "text-blue-600 bg-blue-50" : "text-gray-700 hover:text-blue-600"}`}
      >
        <img
          src={`${getBaseUrl()}/static/images/canva.png`}
          className="w-4 h-4 object-contain"
          alt="Canva logo"
        />
      </button>

      {/* Dropdown panel — fixed to escape modal overflow */}
      {open && (
        <div
          ref={panelRef}
          className="fixed z-[200] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
          style={{ top: pos.top, left: pos.left, minWidth: 250 }}
        >
          <CanvaDesign
            projectId={projectId}
            onExportImage={async (filename, file) => {
              insertImageIntoQuill(quillRef.current, file);
              setOpen(false);
            }}
            navigateToUrl={(url) => {
              navigateToUrl(url);
              setOpen(false);
            }}
            className="w-[450px]"
          />
        </div>
      )}
    </span>
  );
};

export default EmailCanvaPicker;
